/**
 * Fonte della segnalazione di un Ticket manuale: come è stata rilevata (advisory, CERT, cliente,
 * ricercatore…), quando è arrivata e — se serve ricontattare — chi l'ha segnalata.
 *
 * `receivedAt` è la data di awareness del CRA (Art. 14: gli SLA decorrono da quando il
 * fabbricante ne viene a conoscenza), non quella di inserimento nel portale. Il contatto è un
 * dato personale: per i clienti si sceglie dall'anagrafica (si salvano solo gli id, l'email non
 * viene duplicata), per le altre fonti si inserisce a mano ed è facoltativo.
 */

import { Select } from '../../components/Select.js';
import { useCustomer, useContacts, useCustomers } from '../customers/hooks.js';
import {
  TICKET_SOURCE_LABELS,
  type CreateTicketInput,
  type TicketSource,
  type TicketSourceType,
} from './types.js';
import styles from './Triage.module.css';

export interface SorgenteForm {
  tipo: TicketSourceType | '';
  riferimento: string;
  /** Valore di <input type="datetime-local">, ora locale. */
  ricevutaIl: string;
  /** Solo per `customer`: contatto dall'anagrafica o inserito a mano. */
  modoContatto: 'anagrafica' | 'libero';
  clienteId: string;
  /** Id del contatto in anagrafica, '' (nessuno) o NUOVO_CONTATTO. */
  contattoId: string;
  /** Email del nuovo contatto da registrare tra quelli del cliente (contattoId = NUOVO_CONTATTO). */
  emailNuovo: string;
  /** Contatto libero (non finisce in anagrafica). */
  nome: string;
  email: string;
  azienda: string;
}

/** Valore speciale del menu contatti: "aggiungi un nuovo contatto al cliente". */
export const NUOVO_CONTATTO = 'nuovo';

const pad = (n: number) => String(n).padStart(2, '0');

/** Data/ora locale nel formato di datetime-local (minuti). */
export function adessoLocale(d = new Date()): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export const sorgenteIniziale = (): SorgenteForm => ({
  tipo: '',
  riferimento: '',
  ricevutaIl: adessoLocale(),
  modoContatto: 'anagrafica',
  clienteId: '',
  contattoId: '',
  emailNuovo: '',
  nome: '',
  email: '',
  azienda: '',
});

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Tipi di fonte per cui ha senso raccogliere un contatto libero. */
const CONTATTO_LIBERO = new Set<TicketSourceType>(['researcher', 'cert_csirt', 'other']);

/**
 * Il form è valido? Nessun campo è obbligatorio (senza tipo la fonte non viene inviata), ma
 * quelli compilati devono essere coerenti: data non futura, email ben formata.
 */
export function sorgenteValida(f: SorgenteForm): boolean {
  if (f.tipo === '') return true;
  if (f.ricevutaIl !== '' && new Date(f.ricevutaIl).getTime() > Date.now() + 60_000) return false;
  if (f.tipo === 'customer' && f.modoContatto === 'anagrafica') {
    const nuovo = f.contattoId === NUOVO_CONTATTO && f.emailNuovo.trim() !== '';
    return !nuovo || EMAIL_RE.test(f.emailNuovo.trim());
  }
  const libero = f.tipo === 'customer' || CONTATTO_LIBERO.has(f.tipo);
  if (libero && f.email.trim() !== '' && !EMAIL_RE.test(f.email.trim())) return false;
  return true;
}

/**
 * Form → fonte del body POST (solo i campi pertinenti al tipo scelto). Senza tipo non c'è
 * fonte; senza data il server usa l'ora di creazione.
 */
export function sorgenteInInput(f: SorgenteForm): CreateTicketInput['source'] | undefined {
  if (f.tipo === '') return undefined;
  const riferimento = f.riferimento.trim();
  let reporter: NonNullable<CreateTicketInput['source']>['reporter'];
  if (f.tipo === 'customer' && f.modoContatto === 'anagrafica') {
    if (f.clienteId !== '') {
      const nuova = f.contattoId === NUOVO_CONTATTO ? f.emailNuovo.trim() : '';
      reporter = {
        customerId: Number(f.clienteId),
        ...(f.contattoId !== '' && f.contattoId !== NUOVO_CONTATTO
          ? { contactId: Number(f.contattoId) }
          : {}),
        ...(nuova ? { email: nuova } : {}),
      };
    }
  } else if (f.tipo === 'customer' || CONTATTO_LIBERO.has(f.tipo)) {
    const nome = f.nome.trim();
    const email = f.email.trim();
    const azienda = f.azienda.trim();
    if (nome || email || azienda) {
      reporter = {
        ...(nome ? { name: nome } : {}),
        ...(email ? { email } : {}),
        ...(azienda ? { company: azienda } : {}),
      };
    }
  }
  return {
    type: f.tipo,
    ...(riferimento ? { reference: riferimento } : {}),
    ...(f.ricevutaIl !== '' ? { receivedAt: new Date(f.ricevutaIl).toISOString() } : {}),
    ...(reporter ? { reporter } : {}),
  };
}

const PLACEHOLDER_RIFERIMENTO: Record<TicketSourceType, string> = {
  vendor_advisory: 'URL o ID dell’advisory (es. GHSA-xxxx-xxxx-xxxx)',
  cert_csirt: 'ID o URL della segnalazione del CERT',
  customer: 'Oggetto della mail o numero di ticket del cliente',
  researcher: 'Oggetto della mail o link alla segnalazione',
  internal: 'Es. audit interno, pentest, scan',
  other: 'Come è arrivata la segnalazione',
};

function ContattiCliente({
  clienteId,
  contattoId,
  emailNuovo,
  onCliente,
  onContatto,
  onEmailNuovo,
}: {
  clienteId: string;
  contattoId: string;
  emailNuovo: string;
  /** Cambiare cliente azzera il contatto: un solo aggiornamento di stato. */
  onCliente: (clienteId: string) => void;
  onContatto: (contattoId: string) => void;
  onEmailNuovo: (email: string) => void;
}) {
  const { data: clienti, isLoading } = useCustomers();
  const { data: contatti } = useContacts(clienteId === '' ? 0 : Number(clienteId));
  const nomeCliente = clienti?.find((c) => String(c.id) === clienteId)?.name;
  const nuovo = contattoId === NUOVO_CONTATTO;
  const emailNonValida = nuovo && emailNuovo.trim() !== '' && !EMAIL_RE.test(emailNuovo.trim());
  return (
    <>
      <Select
        ariaLabel="Cliente"
        value={clienteId}
        onChange={onCliente}
        options={(clienti ?? []).map((c) => ({ value: String(c.id), label: c.name }))}
        placeholder={isLoading ? 'Caricamento clienti…' : 'Seleziona il cliente…'}
        testId="sorgente-cliente"
      />
      {clienteId !== '' && (
        <Select
          ariaLabel="Contatto del cliente"
          value={contattoId}
          onChange={onContatto}
          options={[
            { value: '', label: 'Nessun contatto specifico' },
            ...(contatti ?? []).map((c) => ({
              value: String(c.id),
              label: `${c.email} (${c.role})`,
            })),
            { value: NUOVO_CONTATTO, label: 'Aggiungi un nuovo contatto…' },
          ]}
          testId="sorgente-contatto"
        />
      )}
      {nuovo && (
        <>
          <input
            type="email"
            className={styles.formSelect}
            aria-label="Email del nuovo contatto"
            aria-invalid={emailNonValida}
            value={emailNuovo}
            maxLength={254}
            onChange={(e) => onEmailNuovo(e.target.value)}
            placeholder="Email di chi ha segnalato"
            data-testid="sorgente-email-nuovo"
          />
          {emailNonValida && (
            <p className={styles.inlineMutError} role="alert">
              Email non valida.
            </p>
          )}
          <p className={styles.pickerHint}>
            Verrà salvata tra i contatti di {nomeCliente ?? 'questa azienda'} con il ruolo
            «segnalante»: resta ricontattabile, ma non riceve le notifiche di vulnerabilità.
          </p>
        </>
      )}
    </>
  );
}

export function SorgenteFields({
  value,
  onChange,
}: {
  value: SorgenteForm;
  onChange: (next: SorgenteForm) => void;
}) {
  const set = <K extends keyof SorgenteForm>(k: K, v: SorgenteForm[K]) =>
    onChange({ ...value, [k]: v });
  const { tipo } = value;
  const anagrafica = tipo === 'customer' && value.modoContatto === 'anagrafica';
  const libero = tipo === 'customer' ? !anagrafica : tipo !== '' && CONTATTO_LIBERO.has(tipo);
  const emailNonValida = libero && value.email.trim() !== '' && !EMAIL_RE.test(value.email.trim());

  return (
    <fieldset className={styles.sorgente} data-testid="sorgente-ticket">
      <legend className={styles.formGroupLabel}>Origine della segnalazione</legend>

      <Select
        ariaLabel="Come è stata rilevata"
        value={tipo}
        onChange={(v) => set('tipo', v as SorgenteForm['tipo'])}
        options={[
          { value: '', label: 'Non specificata' },
          ...(Object.keys(TICKET_SOURCE_LABELS) as TicketSourceType[]).map((t) => ({
            value: t,
            label: TICKET_SOURCE_LABELS[t],
          })),
        ]}
        testId="sorgente-tipo"
      />

      {tipo !== '' && (
        <>
          <input
            type="text"
            className={styles.formSelect}
            aria-label="Riferimento"
            value={value.riferimento}
            maxLength={500}
            onChange={(e) => set('riferimento', e.target.value)}
            placeholder={PLACEHOLDER_RIFERIMENTO[tipo]}
            data-testid="sorgente-riferimento"
          />

          <label className={styles.sorgenteRiga}>
            <span className={styles.formGroupLabel}>Ricevuta il</span>
            <input
              type="datetime-local"
              className={styles.formSelect}
              value={value.ricevutaIl}
              max={adessoLocale()}
              onChange={(e) => set('ricevutaIl', e.target.value)}
              data-testid="sorgente-ricevuta-il"
            />
          </label>
          <p className={styles.pickerHint}>
            Quando la segnalazione è arrivata, non quando la inserisci.
          </p>

          {(tipo === 'customer' || CONTATTO_LIBERO.has(tipo)) && (
            <div className={styles.sorgenteContatto}>
              <span className={styles.formGroupLabel}>Chi ha segnalato (facoltativo)</span>

              {tipo === 'customer' && (
                <div className={styles.pickerBulk} role="group" aria-label="Origine del contatto">
                  {(
                    [
                      ['anagrafica', 'Da anagrafica'],
                      ['libero', 'Contatto libero'],
                    ] as const
                  ).map(([modo, etichetta]) => (
                    <button
                      key={modo}
                      type="button"
                      className={styles.pickerBulkButton}
                      aria-pressed={value.modoContatto === modo}
                      onClick={() => set('modoContatto', modo)}
                      data-testid={`sorgente-modo-${modo}`}
                    >
                      {etichetta}
                    </button>
                  ))}
                </div>
              )}

              {anagrafica ? (
                <ContattiCliente
                  clienteId={value.clienteId}
                  contattoId={value.contattoId}
                  emailNuovo={value.emailNuovo}
                  onCliente={(v) =>
                    onChange({ ...value, clienteId: v, contattoId: '', emailNuovo: '' })
                  }
                  onContatto={(v) => set('contattoId', v)}
                  onEmailNuovo={(v) => set('emailNuovo', v)}
                />
              ) : (
                <>
                  <input
                    type="text"
                    className={styles.formSelect}
                    aria-label="Nome del segnalante"
                    value={value.nome}
                    maxLength={200}
                    onChange={(e) => set('nome', e.target.value)}
                    placeholder="Nome"
                    data-testid="sorgente-nome"
                  />
                  <input
                    type="email"
                    className={styles.formSelect}
                    aria-label="Email del segnalante"
                    aria-invalid={emailNonValida}
                    value={value.email}
                    maxLength={254}
                    onChange={(e) => set('email', e.target.value)}
                    placeholder="Email"
                    data-testid="sorgente-email"
                  />
                  {emailNonValida && (
                    <p className={styles.inlineMutError} role="alert">
                      Email non valida.
                    </p>
                  )}
                  <input
                    type="text"
                    className={styles.formSelect}
                    aria-label="Azienda di chi ha segnalato"
                    value={value.azienda}
                    maxLength={200}
                    onChange={(e) => set('azienda', e.target.value)}
                    placeholder="Azienda / organizzazione"
                    data-testid="sorgente-azienda"
                  />
                  <p className={styles.pickerHint}>
                    Contatto libero: resta solo in questo ticket, non viene aggiunto ai contatti di
                    nessuna azienda.
                  </p>
                </>
              )}
              <p className={styles.pickerHint}>
                Dato personale: serve per ricontattare chi ha segnalato. Lo vede solo chi può
                analizzare i ticket e non viene inviato a ENISA né incluso negli export.
              </p>
            </div>
          )}
        </>
      )}
    </fieldset>
  );
}

// ---------------------------------------------------------------------------
// Dettaglio
// ---------------------------------------------------------------------------

/** "8 ott 2026, 15:55": giorno, mese breve, ora e minuti (i secondi sono rumore). */
export function formattaDataOra(iso: string): string {
  return new Date(iso).toLocaleString('it-IT', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** "2 ore fa", "ieri": per il tooltip, dove aiuta a capire da quanto è aperta la segnalazione. */
function tempoRelativo(iso: string): string {
  const minuti = Math.round((new Date(iso).getTime() - Date.now()) / 60_000);
  const rtf = new Intl.RelativeTimeFormat('it', { numeric: 'auto' });
  if (Math.abs(minuti) < 60) return rtf.format(minuti, 'minute');
  if (Math.abs(minuti) < 60 * 24) return rtf.format(Math.round(minuti / 60), 'hour');
  return rtf.format(Math.round(minuti / (60 * 24)), 'day');
}

function SegnalanteDaAnagrafica({
  customerId,
  contactId,
}: {
  customerId: number;
  contactId?: number;
}) {
  const { data: cliente, isLoading } = useCustomer(customerId);
  const { data: contatti } = useContacts(contactId === undefined ? 0 : customerId);
  if (isLoading) return <>…</>;
  const contatto = contactId === undefined ? undefined : contatti?.find((c) => c.id === contactId);
  return (
    <>
      {cliente?.name ?? `Cliente #${customerId}`}
      {contactId !== undefined && (
        <span className={styles.campoSecondario}>
          {contatto ? contatto.email : 'contatto non più in anagrafica'}
        </span>
      )}
    </>
  );
}

/**
 * Fonte del ticket come elenco etichetta → valore (il contenitore è la scheda "Origine").
 * Il contatto compare solo se il server lo invia (permesso di analisi).
 */
export function SorgenteDettaglio({
  ticketId,
  source,
}: {
  ticketId: number;
  source: TicketSource;
}) {
  const r = source.reporter;
  // Segnalante libero: nome (e azienda) in evidenza, email sotto; senza nome vale l'email.
  const persona =
    r && r.customerId === undefined
      ? [r.name, r.company && `(${r.company})`].filter(Boolean).join(' ') || r.email
      : undefined;
  return (
    <dl className={styles.campi} data-testid={`sorgente-ticket-${ticketId}`}>
      <dt className={styles.campoEtichetta}>Fonte</dt>
      <dd className={styles.campoValore}>{TICKET_SOURCE_LABELS[source.type]}</dd>

      {source.reference && (
        <>
          <dt className={styles.campoEtichetta}>Riferimento</dt>
          <dd className={styles.campoValore}>{source.reference}</dd>
        </>
      )}

      <dt className={styles.campoEtichetta}>Ricevuta</dt>
      <dd className={styles.campoValore}>
        <time
          dateTime={source.receivedAt}
          title={`${new Date(source.receivedAt).toLocaleString('it-IT')} · ${tempoRelativo(source.receivedAt)}`}
        >
          {formattaDataOra(source.receivedAt)}
        </time>
      </dd>

      {r && (
        <>
          <dt className={styles.campoEtichetta}>Segnalante</dt>
          <dd className={styles.campoValore} data-testid={`segnalante-ticket-${ticketId}`}>
            {r.customerId !== undefined ? (
              <SegnalanteDaAnagrafica customerId={r.customerId} contactId={r.contactId} />
            ) : (
              <>
                {persona}
                {r.email && persona !== r.email && (
                  <span className={styles.campoSecondario}>{r.email}</span>
                )}
              </>
            )}
          </dd>
        </>
      )}
    </dl>
  );
}
